import { useState, useCallback } from 'react'
import { Tab, TabManager, WindowType } from '../types/windows'
import { CIWWindow } from '../windows/CIW'
import { LibraryManagerWindow } from '../windows/LibraryManager'
import { AdminDashboard } from '../windows/AdminDashboard'

// Generate unique ID for tabs
const generateTabId = (): string => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Default tabs that cannot be closed
const createDefaultTabs = (isAdmin?: boolean): Tab[] => {
  const tabs: Tab[] = [
    {
      id: 'ciw',
      title: 'CIW',
      type: 'ciw',
      closable: false,
      active: false,
      component: CIWWindow,
    },
    {
      id: 'library',
      title: 'Library Manager',
      type: 'library',
      closable: false,
      active: true,
      component: LibraryManagerWindow,
    },
  ]

  if (isAdmin) {
    tabs.push({
      id: 'admin_panel',
      title: 'Admin Panel',
      type: 'admin_panel',
      closable: false,
      active: false,
      component: AdminDashboard,
    })
  }

  return tabs
}

export const useTabManager = (isAdmin?: boolean): TabManager => {
  const [tabs, setTabs] = useState<Tab[]>(createDefaultTabs(isAdmin))
  const [activeTabId, setActiveTabId] = useState<string>('library')

  // Switch to a specific tab
  const switchTab = useCallback((tabId: string): void => {
    setTabs(prevTabs =>
      prevTabs.map(tab => ({
        ...tab,
        active: tab.id === tabId,
      }))
    )
    setActiveTabId(tabId)
  }, [])

  // Add a new tab with duplicate prevention
  const addTab = useCallback((tabData: Omit<Tab, 'id' | 'active'>): string => {
    // Check for existing tab with same type and project
    const existingTab = tabs.find(tab => {
      if (tab.type !== tabData.type) return false

      // For project-specific tabs (STL_IN, STL_OUT), check projectId
      if ((tabData.type === 'stl_in' || tabData.type === 'stl_out') &&
          tabData.props?.projectId && tab.props?.projectId) {
        return tab.props.projectId === tabData.props.projectId
      }

      // For Girasoles View, allow multiple instances
      if (tabData.type === 'girasoles_view') {
        return false // Always allow new Girasoles View tabs
      }

      return false
    })

    // If existing tab found, focus it instead of creating new one
    if (existingTab) {
      console.log(`Tab already exists for ${tabData.type} with project ${tabData.props?.projectId}, focusing existing tab`)
      switchTab(existingTab.id)
      return existingTab.id
    }

    const newTabId = generateTabId()
    const newTab: Tab = {
      ...tabData,
      id: newTabId,
      active: false,
    }

    setTabs(prevTabs => {
      // Deactivate all tabs
      const updatedTabs = prevTabs.map(tab => ({ ...tab, active: false }))
      return [...updatedTabs, newTab]
    })

    // Switch to the new tab
    setActiveTabId(newTabId)

    return newTabId
  }, [tabs, switchTab])

  // Remove a tab (only if closable)
  const removeTab = useCallback((tabId: string): void => {
    setTabs(prevTabs => {
      const tabToRemove = prevTabs.find(tab => tab.id === tabId)
      
      // Don't remove non-closable tabs
      if (!tabToRemove || !tabToRemove.closable) {
        console.warn(`Cannot remove tab ${tabId}: tab is not closable`)
        return prevTabs
      }

      const filteredTabs = prevTabs.filter(tab => tab.id !== tabId)
      
      // If we're removing the active tab, switch to the first available tab
      if (activeTabId === tabId && filteredTabs.length > 0) {
        setActiveTabId(filteredTabs[0].id)
      }

      return filteredTabs
    })
  }, [activeTabId])

  // Get the currently active tab
  const getActiveTab = useCallback((): Tab | null => {
    return tabs.find(tab => tab.id === activeTabId) || null
  }, [tabs, activeTabId])

  // Update tab properties
  const updateTab = useCallback((tabId: string, updates: Partial<Tab>): void => {
    setTabs(prevTabs =>
      prevTabs.map(tab =>
        tab.id === tabId ? { ...tab, ...updates } : tab
      )
    )
  }, [])

  // Find existing tab by type and project
  const findExistingTab = useCallback((type: WindowType, projectId?: string): Tab | null => {
    return tabs.find(tab => {
      if (tab.type !== type) return false

      // For project-specific tabs, check projectId
      if ((type === 'stl_in' || type === 'stl_out') && projectId && tab.props?.projectId) {
        return tab.props.projectId === projectId
      }

      return tab.type === type
    }) || null
  }, [tabs])

  // Focus existing tab or create new one
  const focusOrCreateTab = useCallback((tabData: Omit<Tab, 'id' | 'active'>): string => {
    const existingTab = findExistingTab(tabData.type, tabData.props?.projectId)

    if (existingTab) {
      switchTab(existingTab.id)
      return existingTab.id
    }

    return addTab(tabData)
  }, [findExistingTab, switchTab, addTab])

  return {
    tabs,
    activeTabId,
    addTab,
    removeTab,
    switchTab,
    getActiveTab,
    updateTab,
    findExistingTab,
    focusOrCreateTab,
  }
}
